import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Info,
  FileText,
  Film,
  ScrollText,
  Loader2,
  Clock,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { getSharedEpisode, getSharedShortDownloadLink } from '../lib/valuebellApi';
import { getRunStatus, TONE_CLASSES } from '../lib/runHelpers';
import { Toaster } from 'sonner';
import { Footer } from './Footer';

import { InfoTab } from './episode-tabs/InfoTab';
import { ShowNotesTab } from './episode-tabs/ShowNotesTab';
import { TranscriptTab } from './episode-tabs/TranscriptTab';
import { ShortsTab } from './episode-tabs/ShortsTab';

const STATUS_ICON = {
  pending: Clock,
  queued: Clock,
  transcribing: Loader2,
  mapping: Loader2,
  'creating-shorts': Loader2,
  done: CheckCircle2,
  error: AlertCircle,
};

export const SharedEpisodePage = () => {
  const { token } = useParams();
  const [activeTab, setActiveTab] = useState('info');

  const { data: run, isLoading, error } = useQuery({
    queryKey: ['shared-episode', token],
    queryFn: () => getSharedEpisode(token),
  });

  const TabButton = ({ value, icon: Icon, label }) => {
    const active = activeTab === value;
    return (
      <button
        onClick={() => setActiveTab(value)}
        className={`flex-1 sm:flex-none flex items-center justify-center sm:justify-start gap-1.5 sm:gap-3 px-2.5 sm:px-4 py-2 sm:py-3 rounded-xl transition-all duration-200 text-xs sm:text-base ${
          active
            ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
            : 'text-slate-400 hover:text-white hover:bg-brand-surface-2'
        }`}
      >
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
        <span className="font-medium">{label}</span>
      </button>
    );
  };

  // Minimal header for shared page
  const SharedHeader = () => (
    <header className="sticky top-0 z-40 bg-brand-bg/80 backdrop-blur-lg border-b border-brand-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <Link to="/" className="flex items-center gap-2">
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="ValueBell Logo"
              className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
            />
            <span className="text-lg sm:text-xl font-bold text-white">VALUEBOT</span>
          </Link>
          <a
            href="https://www.valuebell.studio/contact"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-200"
          >
            Contact Us
          </a>
        </div>
      </div>
    </header>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-bg flex flex-col">
        <SharedHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-orange-400 animate-spin" />
            <p className="text-slate-300">Loading episode…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="min-h-screen bg-brand-bg flex flex-col">
        <SharedHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-brand-surface border border-brand-surface-3 rounded-2xl p-8 sm:p-12 max-w-md text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Episode not found</h2>
            <p className="text-slate-400">
              This share link may be invalid or the episode is no longer available.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const status = getRunStatus(run);
  const tone = TONE_CLASSES[status.tone] || TONE_CLASSES.amber;
  const StatusIcon = STATUS_ICON[status.key] || Clock;

  return (
    <div className="min-h-screen bg-brand-bg">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute top-[-10%] left-[-5%] w-[55vw] h-[55vw] bg-orange-500/[0.07] rounded-full blur-3xl" />
        <div className="absolute top-[20%] right-[-10%] w-[60vw] h-[60vw] bg-red-500/[0.06] rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[20%] w-[50vw] h-[50vw] bg-orange-500/[0.05] rounded-full blur-3xl" />
      </div>

      <div className="relative z-10">
        <SharedHeader />
        <Toaster position="bottom-center" richColors />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
          <div className="bg-brand-bg border border-brand-surface-3 rounded-2xl overflow-hidden">
            <div className="flex flex-col sm:flex-row h-[85vh]">
              {/* Sidebar */}
              <div className="w-full sm:w-64 bg-brand-surface border-b sm:border-b-0 sm:border-r border-brand-surface-3 p-3 sm:p-6 flex sm:flex-col flex-shrink-0">
                <div className="hidden sm:block mb-8">
                  <h2 className="text-xl font-bold text-white mb-2">Menu</h2>
                  <div className="w-12 h-1 bg-gradient-to-r from-orange-400 to-red-500 rounded-full" />
                </div>

                <nav className="flex sm:flex-col flex-1 gap-1.5 sm:gap-2 w-full">
                  <TabButton value="info" icon={Info} label="Info" />
                  <TabButton value="transcript" icon={ScrollText} label="Transcript" />
                  <TabButton value="notes" icon={FileText} label="Show Notes" />
                  <TabButton value="shorts" icon={Film} label="Shorts" />
                </nav>
              </div>

              {/* Main content */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800">
                {activeTab === 'info' && (
                  <InfoTab run={run} status={status} StatusIcon={StatusIcon} tone={tone} showResultLinks={false} />
                )}

                {activeTab === 'notes' && (
                  <ShowNotesTab run={run} status={status} />
                )}

                {activeTab === 'transcript' && (
                  <TranscriptTab run={run} status={status} />
                )}

                {activeTab === 'shorts' && (
                  <ShortsTab run={run} status={status} getDownloadLink={short => getSharedShortDownloadLink(token, short.shortId)} />
                )}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};
